import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import UnifiedContactModal from './UnifiedContactModal';

interface BannerSettings {
  enabled: boolean;
  eyebrow_text: string;
  headline: string;
  body: string;
  primary_cta_text: string;
  primary_cta_url: string;
  secondary_cta_text: string;
  secondary_cta_url: string;
  phone_text?: string;
  helper_text?: string;
}

const defaultSettings: BannerSettings = {
  enabled: true,
  eyebrow_text: 'SEASONAL DIRTY WATER & INTAKE PROTECTION',
  headline: 'Protect Intakes, Pumps & Filtration During Dirty Water Season.',
  body: 'Runoff, canal turns, and river events can spike debris and turbidity fast. We help growers, districts, and operators choose the right filtration approach and protect equipment—without overbuying.',
  primary_cta_text: 'Dirty Water & Intake Protection Hub',
  primary_cta_url: '/flood-resilience',
  secondary_cta_text: 'Talk to a Specialist',
  secondary_cta_url: '/contact',
  phone_text: 'Or call (509) 901-7476',
  helper_text: 'Typical response: within 1 business day.'
};

const SeasonalSupportBanner = () => {
  const [settings, setSettings] = useState<BannerSettings>(defaultSettings);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('seasonal_banner_settings')
          .select('*')
          .limit(1)
          .maybeSingle();

        if (error) {
          console.warn('Using default banner settings:', error.message);
          return;
        }

        if (data) {
          setSettings(data as BannerSettings);
        }
      } catch (err) {
        console.warn('Using default banner settings due to error:', err);
      }
    };

    fetchBannerSettings();
  }, []);

  if (!settings.enabled) {
    return null;
  }

  const handleSecondaryClick = () => {
    if (settings.secondary_cta_url === '/contact') {
      setIsContactModalOpen(true);
    }
  };

  const SecondaryButton = settings.secondary_cta_url === '/contact' ? 'button' : Link;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="mt-12 lg:mt-16"
      >
        <div className="bg-gradient-to-br from-blue-50 via-white to-amber-50 rounded-2xl p-8 shadow-xl border-2 border-blue-200">
          <div className="flex items-start gap-4">
            <Droplets className="text-ctc-blue flex-shrink-0 mt-1" size={32} />
            <div className="flex-1">
              <div className="text-ctc-blue font-semibold text-sm uppercase tracking-wide mb-2">
                {settings.eyebrow_text}
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                {settings.headline}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {settings.body}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-3">
                <Link
                  to={settings.primary_cta_url}
                  className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-6 py-3 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
                >
                  <span>{settings.primary_cta_text}</span>
                  <ArrowRight size={20} />
                </Link>
                <SecondaryButton
                  {...(settings.secondary_cta_url === '/contact'
                    ? { onClick: handleSecondaryClick }
                    : { to: settings.secondary_cta_url }
                  )}
                  className="border-2 border-ctc-blue text-ctc-blue px-6 py-3 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-2"
                >
                  <Phone size={20} />
                  <span>{settings.secondary_cta_text}</span>
                </SecondaryButton>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-600">
                {settings.phone_text && (
                  <a
                    href={`tel:${settings.phone_text.replace(/[^0-9+]/g, '')}`}
                    className="hover:text-ctc-blue transition-colors"
                  >
                    {settings.phone_text}
                  </a>
                )}
                {settings.phone_text && settings.helper_text && (
                  <span className="hidden sm:inline text-gray-400">•</span>
                )}
                {settings.helper_text && (
                  <span className="italic">{settings.helper_text}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="general"
        formSource="seasonal_banner_specialist_cta"
      />
    </>
  );
};

export default SeasonalSupportBanner;
